import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { RefObject } from 'react';

import { createPortal } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';

import { Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { Control, DivIcon, DomEvent, DomUtil, latLngBounds } from 'leaflet';
import type { Map as LeafletMapInstance, Marker as LeafletMarker } from 'leaflet';
import { useIntl } from 'react-intl';

import LeafletMap from '@src/components/Maps/Map';
import { getInitialsName } from '@src/packages/utils';
import IntlMessages from '@src/util/IntlMessages';

import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../../constants';
import type { ActiveUserListItem, UserLocationTrackerRef } from '../../types';
import styles from './ActiveUserMap.module.less';

interface ActiveUserMapProps {
  users: ActiveUserListItem[];
  currentUser: ActiveUserListItem | null;
  mapRef: RefObject<LeafletMapInstance | null>;
  overlayRef: RefObject<HTMLDivElement>;
  isMobile: boolean;
  focusRequestId: number;
  fitAllRequestId: number;
  fitAllReady: boolean;
  onUserInfo?: (userId: string) => void;
  onMarkerClick?: (userId: string) => void;
  /** Fired when a marker popup closes; used to release the focused user. */
  onMarkerClose?: (userId: string) => void;
}

interface MarkerData extends ActiveUserListItem {
  geoCode: [number, number];
}

interface IconCacheEntry {
  signature: string;
  icon: DivIcon;
}

class FitAllLeafletControl extends Control {
  constructor() {
    super({ position: 'topleft' });
  }

  onAdd(): HTMLElement {
    const container = DomUtil.create('div', 'leaflet-bar');
    DomEvent.disableClickPropagation(container);
    DomEvent.disableScrollPropagation(container);
    return container;
  }
}

interface MapViewport {
  left: number;
  width: number;
  height: number;
}

interface MapViewportControlProps {
  markers: MarkerData[];
  focusedPosition: [number, number] | null;
  focusedUserId?: string;
  focusRequestId: number;
  fitAllRequestId: number;
  fitAllReady: boolean;
  onFocusComplete: (userId: string | undefined) => void;
  overlayRef: RefObject<HTMLDivElement>;
  isMobile: boolean;
}

/** Fit and follow users inside the visible area beside the floating table. */
const MapViewportControl: React.FC<MapViewportControlProps> = ({
  markers,
  focusedPosition,
  focusedUserId,
  focusRequestId,
  fitAllRequestId,
  fitAllReady,
  onFocusComplete,
  overlayRef,
  isMobile,
}) => {
  const map = useMap();
  const intl = useIntl();
  const fitAllLabel = intl.formatMessage({ id: 'activeUserDashboard.map.fitAll' });
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [viewport, setViewport] = useState<MapViewport | null>(null);

  useEffect(() => {
    const mapContainer = map.getContainer();
    const overlay = overlayRef.current;
    const updateViewport = () => {
      map.invalidateSize({ pan: false });
      const mapRect = mapContainer.getBoundingClientRect();
      const overlayRect = !isMobile ? overlay?.getBoundingClientRect() : null;
      const left = overlayRect
        ? Math.min(overlayRect.right - mapRect.left + 48, Math.max(48, mapRect.width - 160))
        : 48;
      setViewport(previous => {
        if (previous?.left === left && previous.width === mapRect.width &&
          previous.height === mapRect.height) return previous;
        return { left, width: mapRect.width, height: mapRect.height };
      });
    };
    updateViewport();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateViewport)
      : null;
    observer?.observe(mapContainer);
    if (overlay) observer?.observe(overlay);
    window.addEventListener('resize', updateViewport);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateViewport);
    };
  }, [map, overlayRef, isMobile]);

  const fitOptions = useMemo(() => ({
    paddingTopLeft: [isMobile ? 24 : viewport?.left ?? 48, isMobile ? 24 : 64] as [number, number],
    paddingBottomRight: [isMobile ? 24 : 48, isMobile ? 120 : 48] as [number, number],
  }), [viewport?.left, isMobile]);

  const handleFitAll = useCallback(() => {
    if (markers.length === 0) return;
    const bounds = latLngBounds(markers.map(marker => marker.geoCode));
    map.fitBounds(bounds, { ...fitOptions, maxZoom: 16 });
  }, [map, markers, fitOptions]);

  // Fit once when markers first become available, then again when the user
  // returns to the overview from a nested route. Resizing the floating list
  // should not reset a map position the user has chosen.
  const lastFitAllRequestRef = useRef<number | null>(null);
  useEffect(() => {
    if (!viewport || focusedPosition || !fitAllReady) return;
    if (markers.length > 0 && lastFitAllRequestRef.current !== fitAllRequestId) {
      lastFitAllRequestRef.current = fitAllRequestId;
      handleFitAll();
    }
  }, [markers.length, handleFitAll, viewport, focusedPosition, fitAllRequestId, fitAllReady]);

  useEffect(() => {
    if (!focusedPosition || !viewport) return;
    let completed = false;
    let fallbackTimer: number | undefined;
    const completeFocus = () => {
      if (completed) return;
      completed = true;
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      onFocusComplete(focusedUserId);
    };
    map.stop();
    map.once('moveend', completeFocus);
    map.flyToBounds(latLngBounds([focusedPosition]), {
      ...fitOptions,
      // Leave room for the popup above the marker and the collapsed sheet below.
      ...(isMobile ? {
        paddingTopLeft: [24, Math.min(320, Math.max(112, viewport.height - 200))] as [number, number],
      } : {}),
      // The previous zoom-out must not limit how closely we can focus a user.
      maxZoom: 18,
      duration: 0.55,
      easeLinearity: 0.25,
    });
    // Leaflet may skip `moveend` when the selected marker is already centered.
    fallbackTimer = window.setTimeout(completeFocus, 700);
    return () => {
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      map.off('moveend', completeFocus);
    };
  }, [map, focusedPosition, focusedUserId, focusRequestId, viewport, fitOptions, onFocusComplete, isMobile]);

  useEffect(() => {
    const control = new FitAllLeafletControl();
    control.addTo(map);
    setContainer(control.getContainer() ?? null);

    return () => {
      control.remove();
      setContainer(null);
    };
  }, [map]);

  if (!container) return null;

  return createPortal(
    <Tooltip title={<IntlMessages id="activeUserDashboard.map.fitAll" />} placement="right">
      <button
        type="button"
        className={styles.fitAllButton}
        onClick={handleFitAll}
        aria-label={fitAllLabel}
      >
        <span className="material-icons-outlined" aria-hidden="true">
          zoom_out_map
        </span>
      </button>
    </Tooltip>,
    container,
  );
};

/** Keep initials visible when a marker photo cannot load in Leaflet's static HTML. */
const buildIconMarkup = (user: ActiveUserListItem, isSelected: boolean): string => {
  const photo = user.photoURL?.trim() ?? '';

  return renderToStaticMarkup(
    <div className={classNames(styles.markerWrap, isSelected && styles.markerSelected)}>
      <span className={styles.markerAvatar}>
        {getInitialsName(user.displayName)}
        {photo ? <img className={styles.markerPhoto} src={photo} alt="" /> : null}
      </span>
      <span className={classNames(styles.markerStatus, user.connected ? styles.online : styles.offline)} data-marker-status="" />
    </div>,
  );
};

interface ActiveUserMarkerProps {
  user: MarkerData;
  icon: DivIcon;
  onUserInfo?: (userId: string) => void;
  onMarkerClick?: (userId: string) => void;
  onMarkerClose?: (userId: string) => void;
  registerMarker: (userId: string, marker: LeafletMarker | null) => void;
}

/**
 * One user marker with referentially stable leaflet props: the position array
 * only changes identity when the coordinates actually change, so re-renders of
 * the dashboard never rebuild marker DOM (which would refetch avatar images).
 */
const ActiveUserMarker: React.FC<ActiveUserMarkerProps> = ({
  user,
  icon,
  onMarkerClick,
  onMarkerClose,
  onUserInfo,
  registerMarker,
}) => {
  const intl = useIntl();
  const [lat, lng] = user.geoCode;
  const position = useMemo<[number, number]>(() => [lat, lng], [lat, lng]);
  const markerRef = useRef<LeafletMarker | null>(null);

  const eventHandlers = useMemo(
    () => ({
      add: () => {
        const image = markerRef.current?.getElement()?.querySelector('img');
        if (!image) return;
        const hideBrokenImage = () => {
          image.style.display = 'none';
        };
        image.addEventListener('error', hideBrokenImage, { once: true });
        if (image.complete && image.naturalWidth === 0) hideBrokenImage();
      },
      click: () => onMarkerClick?.(user.id),
      popupclose: () => onMarkerClose?.(user.id),
    }),
    [onMarkerClick, onMarkerClose, user.id],
  );


  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={eventHandlers}
      ref={element => {
        markerRef.current = element;
        registerMarker(user.id, element);
      }}
    >
      <Popup className={styles.userPopup} maxWidth={360} autoPan={false}>
        <div className={styles.popupIdentity}>
          {user.photoURL ? (
            <img
              className={styles.popupAvatar}
              src={user.photoURL}
              alt=""
              onError={event => {
                event.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <div className={styles.popupIdentityText}>
            <strong className={styles.popupName}>{user.displayName}</strong>
            <span className={styles.popupDocument}>
              <IntlMessages id="activeUserDashboard.user.idCard" />: {user.idCard || '—'}
            </span>
          </div>
        </div>
        <div className={styles.popupStatusRow}>
          <span className={styles.popupConnection}>
            <span className={classNames(styles.connectionDot, user.connected ? styles.online : styles.offline)} />
            <IntlMessages id={user.connected ? 'common.connected' : 'users.info.device.connectivity.disconnected'} />
          </span>
          <span><IntlMessages id="users.info.device.battery" />: <strong>{user.battery === null ? '—' : `${user.battery} %`}</strong></span>
        </div>
        <div className={styles.popupPosition}>
          <span><IntlMessages id="users.info.device.lastPosition" /></span>
          <strong>{user.positionTimeFormated || '—'}</strong>
        </div>
        <dl className={styles.popupMetrics}>
          <div><dt><IntlMessages id="common.altitude" /></dt><dd>{intl.formatNumber(user.altitude)} m</dd></div>
          <div><dt><IntlMessages id="common.accuracy" /></dt><dd>{intl.formatNumber(user.accuracy)} m</dd></div>
          <div><dt><IntlMessages id="common.speed" /></dt><dd>{intl.formatNumber(user.speed)} km/h</dd></div>
        </dl>
        {onUserInfo && <Button className={styles.profileButton} onClick={() => onUserInfo(user.id)}>
          <InfoCircleOutlined aria-hidden="true" />
          <IntlMessages id="activeUserDashboard.user.profile" />
        </Button>}
      </Popup>
    </Marker>
  );
};

const ActiveUserMap = React.forwardRef<UserLocationTrackerRef, ActiveUserMapProps>(
  ({ users, currentUser, focusRequestId, fitAllRequestId, fitAllReady, mapRef, overlayRef, isMobile, onMarkerClick, onMarkerClose, onUserInfo }, ref) => {
    const markerRefs = useRef<Record<string, LeafletMarker | null>>({});
    const markerClusterRef = useRef<React.ElementRef<typeof MarkerClusterGroup>>(null);
    const pendingPopupUserIdRef = useRef<string | null>(null);
    const iconCacheRef = useRef<Map<string, IconCacheEntry>>(new Map());

    const markers = useMemo<MarkerData[]>(
      () =>
        users
          .filter(user => user.positions !== null)
          .map(user => ({ ...user, geoCode: user.positions as [number, number] })),
      [users],
    );

    /**
     * DivIcons are cached by their visual state, including connectivity, so a
     * selected marker keeps the same status as its table row.
     */
    const getIcon = useCallback(
      (user: ActiveUserListItem): DivIcon => {
        const isSelected = user.id === currentUser?.id;
        const signature = [user.photoURL, user.displayName, user.connected, isSelected].join('|');
        const cached = iconCacheRef.current.get(user.id);

        if (cached && cached.signature === signature) {
          return cached.icon;
        }

        const icon = new DivIcon({
          html: buildIconMarkup(user, isSelected),
          className: '',
          iconSize: [40, 40],
          iconAnchor: [20, 20],
          popupAnchor: [0, -20],
        });
        iconCacheRef.current.set(user.id, { signature, icon });
        return icon;
      },
      [currentUser?.id],
    );

    const registerMarker = useCallback((userId: string, marker: LeafletMarker | null) => {
      markerRefs.current[userId] = marker;
    }, []);

    /**
     * Fly target is memoized by coordinate VALUES (not array identity) so the
     * map only flies when the selection or its actual position changes — not on
     * every realtime tick. Releasing the selection stops following the user.
     */
    const focusedMarker = currentUser
      ? markers.find(marker => marker.id === currentUser.id) ?? null
      : null;
    const focusedLat = focusedMarker?.geoCode[0];
    const focusedLng = focusedMarker?.geoCode[1];
    const userFocusedPosition = useMemo<[number, number] | null>(
      () => (focusedLat != null && focusedLng != null ? [focusedLat, focusedLng] : null),
      [focusedLat, focusedLng],
    );

    const openFocusedPopup = useCallback((userId: string | undefined) => {
      if (!userId || pendingPopupUserIdRef.current !== userId) return;
      const marker = markerRefs.current[userId];
      const markerCluster = markerClusterRef.current;
      if (!marker) return;

      const openPopup = () => {
        if (pendingPopupUserIdRef.current !== userId) return;
        marker.openPopup();
        pendingPopupUserIdRef.current = null;
      };

      if (markerCluster) markerCluster.zoomToShowLayer(marker, openPopup);
      else openPopup();
    }, []);

    const handleMarkerClickAndQueuePopup = useCallback((userId: string) => {
      pendingPopupUserIdRef.current = userId;
      onMarkerClick?.(userId);
    }, [onMarkerClick]);

    const handleMarkerPopupClose = useCallback((userId: string) => {
      if (pendingPopupUserIdRef.current === userId) return;
      onMarkerClose?.(userId);
    }, [onMarkerClose]);

    useImperativeHandle(ref, () => ({
      openUserPopup: (userId: string) => {
        pendingPopupUserIdRef.current = userId;
      },
    }));

    useEffect(() => {
      if (currentUser?.id) {
        pendingPopupUserIdRef.current = currentUser.id;
      }
    }, [currentUser?.id]);

    return (
      <LeafletMap
        className={styles.map}
        mapRef={mapRef}
        center={DEFAULT_MAP_CENTER}
        zoom={DEFAULT_MAP_ZOOM}
        gestureHandling
        showLayerControl={false}
      >
        <MarkerClusterGroup
          ref={markerClusterRef}
          chunkedLoading
          maxClusterRadius={40}
          showCoverageOnHover={false}
        >
          {markers.map(marker => (
            <ActiveUserMarker
              key={marker.id}
              user={marker}
              icon={getIcon(marker)}
              onUserInfo={onUserInfo}
              onMarkerClick={handleMarkerClickAndQueuePopup}
              onMarkerClose={handleMarkerPopupClose}
              registerMarker={registerMarker}
            />
          ))}
        </MarkerClusterGroup>
        <MapViewportControl
          markers={markers}
          focusedPosition={userFocusedPosition}
          focusedUserId={currentUser?.id}
          focusRequestId={focusRequestId}
          fitAllRequestId={fitAllRequestId}
          fitAllReady={fitAllReady}
          onFocusComplete={openFocusedPopup}
          overlayRef={overlayRef}
          isMobile={isMobile}
        />
      </LeafletMap>
    );
  },
);

ActiveUserMap.displayName = 'ActiveUserMap';

export default React.memo(ActiveUserMap);
